//==========================================================
// JWT lifecycle
//
// The API mints a 7-day JWT from /api/Auth against a client id + secret. The
// token is not tied to a user, so it can be re-minted silently at any time.
//
// The app used to fetch it only on the login page and then keep it in
// localStorage forever. Once a tab outlived the token, TokenValidationHandler
// answered every request with a bare 401, every service swallowed that in its
// catch block and returned an empty result -- so the whole back office went
// blank with no error. Logout was hit too, because it is itself an API call.
//
// This module owns the token: it decides when it is stale, refreshes it with at
// most one request in flight, and patches window.fetch so an API call either
// leaves with a live token or is retried once with a fresh one.
//==========================================================

import Constants from './Constants';
import Config from './Config';
import UrlConstant from './URLConstants';

// Treat the token as dead slightly early so a request can never ride one that
// expires while it is in flight.
const EXPIRY_SKEW_SECONDS = 120;

// Captured before the patch so refresh + retry never re-enter the interceptor.
const originalFetch = window.fetch.bind(window);

let refreshPromise = null;

export let getToken = () => {
    const token = localStorage.getItem(Constants.Session.JWT_TOKEN);
    return token === null || token === 'null' || token === 'undefined' ? '' : token;
};

// Reads the `exp` claim without verifying the signature. The server stays the
// authority on validity; this only decides when to refresh pre-emptively.
export let isTokenExpired = (token) => {

    if (!token) return true;

    try {
        const segment = token.split('.')[1];
        if (!segment) return true;

        let base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
        while (base64.length % 4 !== 0) base64 += '=';

        const payload = JSON.parse(window.atob(base64));

        // A token with no expiry claim is the server's business, not ours.
        if (!payload || !payload.exp) return false;

        return payload.exp - EXPIRY_SKEW_SECONDS <= Math.floor(Date.now() / 1000);
    }
    catch (e) {
        // Anything we cannot read is as good as expired.
        return true;
    }
};

// Mints a new token. Concurrent callers share one request, so a page that fires
// twenty requests at load does not fire twenty logins behind them.
export let refreshToken = () => {

    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
        try {
            const response = await originalFetch(UrlConstant.Auth, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(Config.Setting.clientApi),
            });

            if (!response.ok) {
                console.error('JWT refresh rejected with status ' + response.status);
                return '';
            }

            const result = await response.json();
            const token = typeof result === 'string' ? result : (result && (result.JWT || result.Token)) || '';

            if (!token) {
                console.error('JWT refresh returned an empty token');
                return '';
            }

            localStorage.setItem(Constants.Session.JWT_TOKEN, token);
            return token;
        }
        catch (e) {
            console.error('JWT refresh failed', e);
            return '';
        }
        finally {
            refreshPromise = null;
        }
    })();

    return refreshPromise;
};

// Tops up an existing token for callers that read localStorage directly instead
// of going through fetch (the SignalR handshake, for one).
//
// Deliberately does NOT mint a token when there is none: the open receipt pages
// treat "no token" as "logged out", and handing every anonymous visitor one
// would blur that. Minting from nothing stays the job of login and of the 401
// retry below, where it is actually asked for.
export let ensureFreshToken = async () => {

    const token = getToken();
    if (!token) return '';
    if (!isTokenExpired(token)) return token;

    return await refreshToken();
};

const authFailureHandlers = [];

// Fires only when the app genuinely cannot recover: minting a token failed, or
// the server still refused the request after a fresh one.
//
// Ordinary expiry deliberately stays invisible. The JWT is an app-level API key
// (one client id + secret for everybody), not the user's login, so renewing it
// needs nothing from them and interrupting their work for it would be noise.
// What is worth showing is the case below, which used to surface as blank
// screens with nothing in the UI to explain them.
export let onAuthFailure = (handler) => {

    authFailureHandlers.push(handler);

    return () => {
        const at = authFailureHandlers.indexOf(handler);
        if (at !== -1) authFailureHandlers.splice(at, 1);
    };
};

const notifyAuthFailure = (reason) => {
    authFailureHandlers.forEach((handler) => {
        try {
            handler(reason);
        }
        catch (e) {
            console.error('Auth failure handler threw', e);
        }
    });
};

// The user's login is the AuthenticationTicket, not the JWT above. When that
// ticket stops being valid -- a session ended from the back office, or the
// account suspended -- the API still answers HTTP 200 and puts the reason in
// the body, so the status checks below never see it. Every service then
// swallows the envelope and returns an empty result, and the tab keeps
// rendering against missing data until some screen trips over it.
const SESSION_ENDED_CODE = 'F000022';      // ErrorCode.SESSION_EXPIRED
const ACCOUNT_SUSPENDED_CODE = 'F065087';  // User.Error.ACCOUNT_SUSPENDED

// Error envelopes are a few hundred bytes; do not clone a large report payload
// just to look for a code that cannot be in it.
const PEEK_MAX_BYTES = 65536;

let sessionEndNotified = false;

const peekForEndedSession = (response) => {

    // Only meaningful while this tab believes it is signed in. On the login and
    // open receipt pages there is no ticket to lose.
    const ticket = localStorage.getItem(Constants.Session.AUTHENTICATION_TICKET);
    if (!ticket || ticket === 'null' || sessionEndNotified) return;

    if (!response.ok) return;

    const length = Number(response.headers.get('content-length'));
    if (length > PEEK_MAX_BYTES) return;

    // clone() so the caller still receives an unread body.
    response.clone().text().then((body) => {

        if (!body) return;

        const suspended = body.indexOf(ACCOUNT_SUSPENDED_CODE) !== -1;
        if (!suspended && body.indexOf(SESSION_ENDED_CODE) === -1) return;

        // Confirm it is the error envelope and not payload data that happens to
        // carry the same string.
        const result = JSON.parse(body);
        if (!result || result.HasError !== true) return;

        const codes = String(result.ErrorCodeCsv || '');
        if (codes.indexOf(suspended ? ACCOUNT_SUSPENDED_CODE : SESSION_ENDED_CODE) === -1) return;

        sessionEndNotified = true;
        notifyAuthFailure(suspended ? 'account-suspended' : 'session-ended');
    })
    .catch(() => { /* unreadable or not JSON -- nothing to act on */ });
};

const isApiRequest = (url) => {
    const base = Config.Setting.apiURL || '';
    return base !== '' && url.indexOf(base) === 0;
};

// Only the POST that mints a token is exempt -- /Auth/Heartbeat and
// /Auth/settings are ordinary authorised calls and must go through the retry.
const isTokenRequest = (url, method) => {
    const requested = url.split('?')[0].replace(/\/+$/, '');
    const auth = UrlConstant.Auth.replace(/\/+$/, '');
    return requested === auth && String(method).toUpperCase() === 'POST';
};

const applyToken = (input, init, token) => {

    if (typeof Request !== 'undefined' && input instanceof Request) {
        const headers = new Headers(input.headers);
        headers.set('Authorization', 'Bearer ' + token);
        return [new Request(input, { headers }), init];
    }

    const nextInit = Object.assign({}, init || {});
    const headers = new Headers((init && init.headers) || {});
    headers.set('Authorization', 'Bearer ' + token);
    nextInit.headers = headers;

    return [input, nextInit];
};

export let installApiInterceptor = () => {

    if (window.__apiSessionInterceptorInstalled) return;
    window.__apiSessionInterceptorInstalled = true;

    window.fetch = async (input, init) => {

        const isRequestObject = typeof Request !== 'undefined' && input instanceof Request;
        const url = isRequestObject ? input.url : String(input);
        const method = (init && init.method) || (isRequestObject ? input.method : 'GET');

        // Everything that is not our API (S3, Google, Firebase, exchange rates)
        // is none of this module's business.
        if (!isApiRequest(url) || isTokenRequest(url, method)) {
            return originalFetch(input, init);
        }

        // A Request body can only be read once, so keep a spare for the retry.
        const spare = isRequestObject ? input.clone() : null;

        let token = getToken();

        // The common case: the tab was parked long enough that the stored token
        // is already dead. Refresh first rather than spending a doomed request.
        if (token && isTokenExpired(token)) {
            token = (await refreshToken()) || token;
        }

        const attempt = token ? applyToken(input, init, token) : [input, init];
        const response = await originalFetch(attempt[0], attempt[1]);

        if (response.status !== 401) {
            peekForEndedSession(response);
            return response;
        }

        // The server rejected a token we believed was live -- clock skew, a
        // rotated secret, or a token minted before a deploy. Mint one and retry
        // exactly once; never loop, or a genuine 401 spins forever.
        const fresh = await refreshToken();
        if (!fresh) {
            notifyAuthFailure('refresh-failed');
            return response;
        }

        const retry = applyToken(spare || input, init, fresh);
        const retried = await originalFetch(retry[0], retry[1]);

        // A brand new token turned away too -- this is not something retrying
        // fixes, so hand it to the UI instead of failing quietly.
        if (retried.status === 401) notifyAuthFailure('rejected-after-refresh');
        else peekForEndedSession(retried);

        return retried;
    };
};
